import { useTranslation } from 'react-i18next';
import type { LoaderFunction, MetaDescriptor } from 'react-router';
import type BoltMetaArgs from 'types/BoltMetaArgs';

import PageHeader from '~/components/PageHeader';
import useRegion from '~/hooks/useRegion';
import useViewEvent from '~/hooks/useViewEvent';
import { getAlternateTagsForAllRegions } from '~/utils/alternateTags';
import { getRegion } from '~/utils/region';

import AboutUsEnglish from './English';
import AboutUsGerman from './German';

const META_PER_REGION = {
  DE: [
    {
      title: 'Über uns'
    },
    {
      content:
        'Seit 2013 versorgt The Sole Supplier die Sneaker-Community mit den neuesten News und Release-Informationen für den britischen und europäischen Markt.',
      name: 'description'
    }
  ],
  GB: [
    {
      title: 'About Us'
    },
    {
      content:
        'The Sole Supplier was founded in 2013, with the aim to provide accurate trainer news and release information for the UK and European market.',
      name: 'description'
    }
  ]
};

const TITLE_PER_REGION = {
  DE: 'Über The Sole Supplier',
  GB: 'About The Sole Supplier'
};

export const handle = { i18n: ['about'] };

type LoaderData = {
  meta: MetaDescriptor[];
};

export const loader: LoaderFunction = ({ request }) => {
  const region = getRegion(new URL(request.url));

  return {
    meta:
      META_PER_REGION[region.code as keyof typeof META_PER_REGION] ??
      META_PER_REGION.GB
  };
};

export const meta = (args: BoltMetaArgs) => {
  return (args.data as LoaderData).meta.concat(
    getAlternateTagsForAllRegions(args)
  );
};

export default function AboutUs() {
  useViewEvent({ viewType: 'about' });

  const { t } = useTranslation('about');
  const region = useRegion();

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: t('about') }]}
        title={
          TITLE_PER_REGION[region.code as keyof typeof TITLE_PER_REGION] ??
          TITLE_PER_REGION.GB
        }
      />
      <article className="mx-auto my-8 flex max-w-3xl flex-col gap-4 px-4">
        <Content />
      </article>
    </>
  );
}

function Content() {
  const region = useRegion();

  switch (region.code) {
    case 'DE':
      return <AboutUsGerman />;
    default:
      return <AboutUsEnglish />;
  }
}
